"use client";

import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useRouter } from "next/navigation";
import {
  RxDotsHorizontal as DotsHorizontalIcon,
  RxDotsVertical as DotsVerticalIcon,
} from "react-icons/rx";
import { toast } from "sonner";

import { deleteWork } from "@/actions/work";
import type { WorkModel } from "@/api";
import { DeleteConfirmation } from "@/components/dashboard/delete-confirmation";
import { CloneWorkForm } from "@/components/dashboard/forms/clone-work-form";
import { EditWorkForm } from "@/components/dashboard/forms/edit-work-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { queryKeys } from "@/constants/query";
import { useWorkAccessLevels } from "@/hooks/useWorkAccessLevels";
import { cn } from "@/lib/utils";

interface WorkActionsProps {
  workItem: WorkModel;
  // Choose trigger style. 'button' uses a Button, 'icon' uses a span for safe nesting inside other buttons.
  triggerAs?: "button" | "icon";
  /** Merged onto the trigger for size overrides (e.g. compact project header) */
  triggerClassName?: string;
  /** Smaller trigger + icon (e.g. sidebar project list on hover) */
  compact?: boolean;
  /** Vertical ⋮ menu icon (e.g. sidebar project row) instead of horizontal ⋯ */
  verticalEllipsis?: boolean;
}

export function WorkActions({
  workItem,
  triggerAs = "button",
  triggerClassName,
  compact = false,
  verticalEllipsis = false,
}: WorkActionsProps) {
  const { projectId, orgId } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const router = useRouter();
  const { workAccessLevels, isLoading: isWorkAccessLevelsLoading } =
    useWorkAccessLevels();

  // Store the work item in state to prevent it from being null when the modal closes
  const [editingWorkItem, setEditingWorkItem] = useState<WorkModel | null>(
    null,
  );
  const [titleActions, setTitleActions] = useState<React.ReactNode>(null);
  const [cloningWorkItem, setCloningWorkItem] = useState<WorkModel | null>(
    null,
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const triggerSizeClass = compact ? "size-6 p-0" : "size-8 p-0";
  const iconClass = compact ? "size-3.5" : "size-4";
  const MenuDotsIcon = verticalEllipsis ? DotsVerticalIcon : DotsHorizontalIcon;

  // Keep editingWorkItem in sync with workItem when it changes
  React.useEffect(() => {
    if (workItem && modalOpen) {
      setEditingWorkItem(workItem);
    }
  }, [workItem, modalOpen]);

  // Keep cloningWorkItem in sync with workItem when it changes
  React.useEffect(() => {
    if (workItem && cloneModalOpen) {
      setCloningWorkItem(workItem);
    }
  }, [workItem, cloneModalOpen]);

  const handleDelete = async () => {
    try {
      if (workItem.id) {
        await deleteWork(workItem.id);
      }
      toast.success("Work item deleted successfully", {
        duration: 3000,
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.getWorkTreeById(projectId as string),
      });

      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectItems(orgId as string),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectNavItems(orgId as string),
      });
      // Invalidate masterWorkItems query used by sidebar for filtering
      queryClient.invalidateQueries({
        queryKey: ["masterWorkItems", orgId as string],
      });
      if (!workItem.parentId) {
        router.push(`/${orgId}/dashboard`);
      }

      queryClient.invalidateQueries({
        queryKey: queryKeys.getProjectItem(projectId as string),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getWorkItemById(workItem?.id || ""),
      });
    } catch (error) {
      console.error("Failed to delete work item:", error);
      toast.error("Failed to delete work item", {
        duration: 3000,
      });
    } finally {
      setDeleteModalOpen(false);
    }
  };

  return (
    <>
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>
                {editingWorkItem?.parentId
                  ? "Edit Work Item"
                  : "Edit Project Details"}
              </span>
              {!editingWorkItem?.parentId && titleActions && (
                <div className="mr-8 flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Security
                  </span>
                  {titleActions}
                </div>
              )}
            </DialogTitle>
            <DialogDescription>
              Update the details of this{" "}
              {editingWorkItem?.parentId ? "work item" : "project"}.
            </DialogDescription>
          </DialogHeader>
          <EditWorkForm
            open={modalOpen}
            onOpenChange={setModalOpen}
            workItem={editingWorkItem}
            onSuccess={() => {
              setModalOpen(false);
            }}
            parentId={editingWorkItem?.parentId}
            onTitleActionsChange={setTitleActions}
            workAccessLevels={workAccessLevels || []}
            isWorkAccessLevelsLoading={isWorkAccessLevelsLoading}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={cloneModalOpen} onOpenChange={setCloneModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {cloningWorkItem?.parentId
                ? `Clone ${cloningWorkItem?.name} Work Item`
                : `Clone ${cloningWorkItem?.name} Project`}
            </DialogTitle>
            <DialogDescription>
              Create a copy of this{" "}
              {cloningWorkItem?.parentId ? "work item" : "project"}.
            </DialogDescription>
          </DialogHeader>
          <CloneWorkForm
            open={cloneModalOpen}
            onOpenChange={setCloneModalOpen}
            workItem={cloningWorkItem}
            onSuccess={() => {
              setCloneModalOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {triggerAs === "button" ? (
            <Button
              variant="ghost"
              className={cn(triggerSizeClass, triggerClassName)}
            >
              <span className="sr-only">Open menu</span>
              <MenuDotsIcon className={iconClass} />
            </Button>
          ) : (
            // Icon trigger suitable for placing inside other interactive containers (e.g., a card button)
            <span
              role="button"
              aria-label="Open menu"
              className={cn(
                "inline-flex items-center justify-center rounded hover:bg-muted/50",
                compact ? "size-6" : "size-8",
                triggerClassName,
              )}
              // Prevent the parent button (e.g., Kanban card) from receiving this event
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              tabIndex={0}
            >
              <MenuDotsIcon className={iconClass} />
            </span>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => {
              setEditingWorkItem({ ...workItem }); // Create a copy to ensure it's a new reference
              setTimeout(() => setModalOpen(true), 0); // Use setTimeout to ensure state update happens first
            }}
          >
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => {
              setCloningWorkItem({ ...workItem }); // Create a copy to ensure it's a new reference
              setTimeout(() => setCloneModalOpen(true), 0); // Use setTimeout to ensure state update happens first
            }}
          >
            Clone
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteConfirmation
        onConfirm={handleDelete}
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
      />
    </>
  );
}
